import type { useParams } from "@tanstack/react-router";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import type { ZodObject, ZodTypeAny } from "zod";

// ─── Types ───────────────────────────────────────────────────────────────────

export type RoleVariant = "success" | "info" | "neutral";
export type TEffect = "positive" | "negative" | "neutral";
export type TTransactionType = "expense" | "withdrawal";

// ─── Constants ───────────────────────────────────────────────────────────────

export const roleMappers = {
  "workshop-owner": "success",
  "workshop-admin": "info",
  "workshop-user": "neutral",
} as const;

export const ROLE_AVATAR_COLORS = {
  "workshop-owner": "bg-amber-100 text-amber-800",
  "workshop-admin": "bg-sky-100 text-sky-800",
  "workshop-user": "bg-violet-100 text-violet-800",
} as const;

export const DEFAULT_AVATAR_COLOR = "bg-muted text-muted-foreground";

const EMPLOYEE_AVATAR_COLORS = [
  "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400",
  "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400",
  "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
  "bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/30 dark:text-fuchsia-400",
  "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400",
];

// ─── Functions ────────────────────────────────────────────────────────────────

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getSearchParam(key: string): string | null {
  return new URLSearchParams(window.location.search).get(key);
}

export function getSchemaFields<T extends ZodObject<Record<string, ZodTypeAny>>>(
  schema: T,
): (keyof T["shape"])[] {
  return Object.keys(schema.shape) as (keyof T["shape"])[];
}

export function generateUnitCode(lastNumber = 0) {
  const nextNumber = lastNumber + 1;
  const paddedNumber = nextNumber.toString().padStart(5, "0");
  return `UNIT-${paddedNumber}`;
}

export function dedupeArray<T>(array: T[], params: ReturnType<typeof useParams>): T[] {
  const paramValues = Object.values(params).filter(Boolean) as T[];
  return Array.from(new Set([...array, ...paramValues]));
}

export function calculatePureGoldWeight(rawWeight: number, karat: number): number {
  if (rawWeight == null || karat == null) return 0;
  return Math.round(rawWeight * (karat / 1000) * 1000) / 1000;
}

export function getInitials(name?: string | null, fallback = "??"): string {
  if (!name?.trim()) return fallback;
  return name
    .trim()
    .split(/\s+/)
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export function formatPhoneForDisplay(phone: string): string {
  if (!phone) return "";
  return phone.replace(/^\+213/, "");
}

export function getEffect(value: number, reverse = false): TEffect {
  const sign = Math.sign(value);
  if (sign === 0 || isNaN(sign)) return "neutral";
  if (reverse) return sign === 1 ? "negative" : "positive";
  return sign === 1 ? "positive" : "negative";
}

export function effectColorMatcher(effect?: TEffect) {
  if (effect === "positive") return "text-emerald-600 dark:text-emerald-500";
  if (effect === "negative") return "text-red-500 dark:text-red-400";
  return "text-foreground";
}

export function getUserAvatarColorClass(role: string): string {
  return ROLE_AVATAR_COLORS[role as keyof typeof ROLE_AVATAR_COLORS] ?? DEFAULT_AVATAR_COLOR;
}

export function createExtraSearchParams<T extends Record<string, any>>(
  params: Partial<Record<keyof T, string>>,
): URLSearchParams {
  return new URLSearchParams(params as Record<string, string>);
}

export function getEmployeeAvatarColor(uid: string): string {
  let hash = 0;
  for (let i = 0; i < uid.length; i++) {
    hash = uid.charCodeAt(i) + ((hash << 5) - hash);
  }
  return EMPLOYEE_AVATAR_COLORS[Math.abs(hash) % EMPLOYEE_AVATAR_COLORS.length];
}
